import {
	IconPassport,
	IconPerson,
	IconTime,
	IconTimelineStop,
} from '@flixbus/honeycomb-icons-react';

import { formatSummaryDateTime } from 'utils/datetime/fnsTimeFormatter';

import { CUSHION_CHOICES } from '../const';
import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';

const LABELS = CREATE_BREAKDOWN_FLOW_TEXT.summary.rowLabels;
const ORIGINAL_DRIVER_ANNOTATION = CREATE_BREAKDOWN_FLOW_TEXT.summary.originalDriverAnnotation;

/**
 * @typedef {import('./summarySectionsRegistry').SummaryRow} SummaryRow
 */

/**
 * Resolve the crew member that actually drives cushioning:
 * - Bus failure base with cushioning=deadheadDriver → deadhead's own crew member.
 * - Every other combination → the original driver (from `data.assignment.crewMember`).
 *
 * @param {object} data
 * @returns {{ id?: string, fullName?: string } | null}
 */
const resolveCushioningDriver = (data) => {
	if (data?.cushioningChoice === CUSHION_CHOICES.deadheadDriver) {
		return data?.deadhead?.crewMember ?? null;
	}
	return data?.assignment?.crewMember ?? null;
};

/**
 * True when the cushioning driver is the original driver — used to render
 * the `(original driver)` annotation on the base variant.
 *
 * The design shows the annotation only when the summary is for a non-parallel
 * variant. Reasoning: in a parallel-ride flow, cushioning is derived off the
 * ride, not the original driver's dedicated window, so the annotation is dropped.
 */
const isCushionedByOriginalDriver = (data) => {
	if (data?.parallelRideChosen) return false;
	if (data?.cushioningChoice === CUSHION_CHOICES.deadheadDriver) return false;
	return true;
};

/**
 * Build the row list for the Summary "Cushioning parameters" accordion.
 *
 * @param {object} data
 * @returns {SummaryRow[]}
 */
export const deriveCushioningRows = (data) => {
	if (!data?.outboundTraveling?.fromTimestamp && !data?.outboundTraveling?.toTimestamp) return [];

	const driver = resolveCushioningDriver(data);
	const annotate = isCushionedByOriginalDriver(data);
	const nameValue = annotate
		? `${driver?.fullName ?? ''} ${ORIGINAL_DRIVER_ANNOTATION}`
		: (driver?.fullName ?? '');

	return [
		{
			label: LABELS.driverName,
			value: nameValue,
			icon: IconPerson,
		},
		{
			label: LABELS.driverId,
			value: driver?.driverCard ?? driver?.id ?? '',
			icon: IconPassport,
		},
		{
			label: LABELS.cushioningStart,
			value: formatSummaryDateTime(data.outboundTraveling?.fromTimestamp),
			icon: IconTime,
		},
		{
			label: LABELS.cushioningEnd,
			value: formatSummaryDateTime(data.outboundTraveling?.toTimestamp),
			icon: IconTimelineStop,
		},
	];
};
